// ATOMS: REQ-REGALO-SNOOPY START
/**
 * CartGiftSnoopy
 * Automatically adds/removes a configured "gift" product to/from the cart
 * whenever:
 *  - the cart total reaches settings.amount_threshold, AND
 *  - at least one non-gift cart line's product has the tag settings.tag_product_promo
 * Re-evaluated on every cart mutation theme-wide by observing #cart-icon-bubble,
 * which every cart-mutating flow (PDP add, cart page, cart drawer, cart
 * notification, quick order list, quick add bulk, upsell checkbox) refreshes
 * via its AJAX "sections" fetch.
 */
class CartGiftSnoopy {
  constructor() {
    this.isProcessing = false;
    this.init();
  }

  init() {
    this.initObserver();
    this.initCartUpdatedListener();
    this.evaluate();
  }

  initObserver() {
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    const observer = new MutationObserver(() => {
      if (this.isProcessing) return;
      this.evaluate();
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  // Safety net: some cart-mutating flows dispatch this event directly
  // (e.g. cart.js's own updateQuantity), catching cases where the
  // #cart-icon-bubble mutation observer might not have fired yet.
  initCartUpdatedListener() {
    document.addEventListener('cartUpdated', () => {
      if (this.isProcessing) return;
      this.evaluate();
    });
  }

  getData() {
    const el = document.getElementById('CartGiftSnoopyData');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('Error parsing CartGiftSnoopyData:', err);
      return null;
    }
  }

  evaluate() {
    const data = this.getData();
    if (!data || !data.enabled || !data.giftVariantId) return;

    const thresholdCents = Math.round(data.amountThreshold * (window.Shopify?.currency?.rate || 1));
    const meetsConditions = data.hasPromoTag && data.cartTotal >= thresholdCents;

    // Out-of-stock gift: skip the add attempt entirely instead of retrying every cycle.
    if (meetsConditions && !data.isGiftInCart && data.giftAvailable !== false) {
      this.addGift(data.giftVariantId);
    } else if (!meetsConditions && data.isGiftInCart) {
      this.removeGift(data.giftLine, data.giftVariantId);
    }
  }

  addGift(variantId) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
      .then((r) => r.json())
      .then(() => {
        this.updateSections();
      })
      .catch((err) => {
        console.error('Error adding gift product to cart:', err);
      })
      .finally(() => {
        setTimeout(() => {
          this.isProcessing = false;
          this.evaluate();
        }, 500);
      });
  }

  removeGift(line, variantId) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const cartItemsInstance = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');

    if (line && cartItemsInstance && typeof cartItemsInstance.updateQuantity === 'function') {
      cartItemsInstance.updateQuantity(line, 0, document.querySelector(`[data-index="${line}"]`));

      setTimeout(() => {
        this.isProcessing = false;
        this.evaluate();
      }, 1000);
      return;
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(line ? { line, quantity: 0 } : { id: variantId, quantity: 0 }),
    })
      .then((r) => r.json())
      .then(() => {
        this.updateSections();
      })
      .catch((err) => {
        console.error('Error removing gift product from cart:', err);
      })
      .finally(() => {
        setTimeout(() => {
          this.isProcessing = false;
          this.evaluate();
        }, 1000);
      });
  }

  // Delegate the main cart list/footer refresh to the theme's own proven
  // `onCartUpdate()` (used for cross drawer/page cart syncing) instead of
  // reimplementing DOM patching, which was silently failing to reliably
  // surface the added/removed gift line after add/remove.
  updateSections() {
    this.isProcessing = true;

    const cartItemsInstance = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (cartItemsInstance && typeof cartItemsInstance.onCartUpdate === 'function') {
      cartItemsInstance.onCartUpdate();
    }

    this.refreshCartIconBubble();

    document.dispatchEvent(new CustomEvent('cartUpdated'));
  }

  refreshCartIconBubble() {
    fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newInner = doc.querySelector('.shopify-section')?.innerHTML;
        const container = document.getElementById('cart-icon-bubble');
        if (container && newInner) {
          container.innerHTML = newInner;
        }
      })
      .catch((err) => console.error('Error updating cart-icon-bubble:', err));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartGiftSnoopyInstance = new CartGiftSnoopy();
});
// ATOMS: REQ-REGALO-SNOOPY END
